(() => {
  if (customElements.get('cavador-logo-motion')) return;

  const preferenceKey = 'cavador-logo-motion-stopped';
  const instances = new Set();
  let stopped = false;
  let button = null;
  let buttonEvents = null;
  let sharedEvents = null;
  let footerObserver = null;

  const readPreference = () => {
    try { return localStorage.getItem(preferenceKey) === 'true'; }
    catch (_) { return false; }
  };

  const update = () => {
    instances.forEach(instance => instance.updateMotion());
    const source = instances.values().next().value;
    if (!source || !button) return;
    const reduced = source.reducedMotion.matches;
    const enabled = !stopped && !reduced;
    button.textContent = enabled ? source.dataset.motionOn : source.dataset.motionOff;
    button.setAttribute('aria-pressed', String(enabled));
    button.disabled = reduced;
    button.title = reduced ? (source.dataset.motionReduced || '') : '';
  };

  const mountControl = () => {
    const source = instances.values().next().value;
    if (!source) return;
    if (button && button.isConnected) { update(); return; }
    buttonEvents?.abort();
    button = null;
    let footer;
    try { footer = document.querySelector(source.dataset.footerSelector || 'footer'); }
    catch (_) { return; }
    if (!footer) {
      if (!footerObserver) {
        footerObserver = new MutationObserver(mountControl);
        footerObserver.observe(document.documentElement, { childList: true, subtree: true });
      }
      return;
    }
    footerObserver?.disconnect();
    footerObserver = null;
    button = document.createElement('button');
    button.type = 'button';
    button.className = 'cavador-logo-motion-toggle';
    buttonEvents = new AbortController();
    button.addEventListener('click', () => {
      stopped = !stopped;
      try { localStorage.setItem(preferenceKey, String(stopped)); } catch (_) { /* Session still works. */ }
      update();
    }, { signal: buttonEvents.signal });
    footer.append(button);
    update();
  };

  const register = instance => {
    if (!instances.size) {
      stopped = readPreference();
      sharedEvents = new AbortController();
      document.addEventListener('shopify:section:load', mountControl, { signal: sharedEvents.signal });
      window.addEventListener('storage', event => {
        if (event.key === preferenceKey || event.key === null) {
          stopped = readPreference();
          update();
        }
      }, { signal: sharedEvents.signal });
    }
    instances.add(instance);
    mountControl();
  };

  const unregister = instance => {
    instances.delete(instance);
    if (instances.size) { update(); return; }
    sharedEvents?.abort();
    buttonEvents?.abort();
    footerObserver?.disconnect();
    button?.remove();
    sharedEvents = buttonEvents = footerObserver = button = null;
  };

  customElements.define('cavador-logo-motion', class extends HTMLElement {
    static get observedAttributes() { return ['data-enabled']; }

    connectedCallback() {
      if (this.events || this.dataset.enabled !== 'true') return;
      this.source = this.querySelector(':scope > img');
      if (!this.source || !this.dataset.motionSrc || !this.dataset.motionOn || !this.dataset.motionOff) return;
      this.events = new AbortController();
      const lifecycle = this.events;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = true;
      this.source.classList.add('cavador-logo-motion__source');
      this.reducedMotion.addEventListener('change', update, { signal: lifecycle.signal });
      document.addEventListener('visibilitychange', () => this.updateMotion(), { signal: lifecycle.signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          this.updateMotion();
        });
        this.observer.observe(this);
      }
      register(this);

      const prepare = () => {
        if (this.events !== lifecycle || lifecycle.signal.aborted || !this.source.naturalWidth || this.clone) return;
        const clone = document.createElement('img');
        clone.style.display = 'none';
        clone.className = 'cavador-logo-motion__monogram';
        clone.alt = '';
        clone.width = 480;
        clone.height = 424;
        clone.decoding = 'async';
        clone.setAttribute('aria-hidden', 'true');
        this.clone = clone;
        this.assetReady = false;
        clone.addEventListener('load', () => {
          if (this.events !== lifecycle || lifecycle.signal.aborted || !clone.naturalWidth ||
              clone.getAttribute('src') !== this.dataset.motionSrc) return;
          this.assetReady = true;
          this.classList.add('is-ready');
          this.updateMotion();
        }, { signal: lifecycle.signal });
        clone.addEventListener('error', () => {
          this.assetReady = false;
          this.classList.remove('is-ready', 'is-motion-enabled', 'is-playing');
        }, { signal: lifecycle.signal });
        this.append(clone);
        this.updateMotion();
      };
      if (this.source.complete) prepare();
      else this.source.addEventListener('load', prepare, { once: true, signal: lifecycle.signal });
    }

    updateMotion() {
      const shouldPlay = !stopped && !this.reducedMotion.matches && this.visible && !document.hidden;
      if (this.clone) {
        if (shouldPlay && !this.clone.hasAttribute('src')) {
          this.clone.setAttribute('src', this.dataset.motionSrc);
        } else if (!shouldPlay && this.clone.hasAttribute('src')) {
          this.clone.removeAttribute('src');
          this.assetReady = false;
          this.classList.remove('is-ready');
        }
      }
      const enabled = shouldPlay && this.assetReady === true;
      this.classList.toggle('is-motion-enabled', enabled);
      this.classList.toggle('is-playing', enabled);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      this.clone?.remove();
      this.source?.classList.remove('cavador-logo-motion__source');
      this.classList.remove('is-ready', 'is-motion-enabled', 'is-playing');
      this.events = this.observer = this.clone = this.source = null;
      this.assetReady = false;
      unregister(this);
    }

    attributeChangedCallback(name, previous, next) {
      if (previous === next || !this.isConnected) return;
      this.disconnectedCallback();
      this.connectedCallback();
    }
  });
})();
